import { useState } from 'react';
import { T, Plural, Num, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import paths from 'src/paths';
import { useAgentDefinitionForm } from './AgentDefinitionFormContext';
import {
  useListAgentRuns,
  useCancelAgentRun,
} from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { Button } from 'src/components/ui/button';
import {
  CircleNotchIcon,
  CaretRightIcon,
  ArrowsClockwiseIcon,
  StopIcon,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { AgentRunSummary } from '@erp/rpc';
import { AgentTraceTable } from 'src/components/agent-trace';
import { StatusDot } from 'src/components/agent-trace/atoms';
import { fmtMs, fmtTokens } from 'src/components/agent-trace/utils';
import { useToast } from 'src/context/ToastContext';
import { cn } from 'src/lib/utils';

// Intl.DateTimeFormat options — kept outside JSX to avoid no-literal-string violations.
const startedAtFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
} as const);

function formatRunStartedAt(startedAt: string): string {
  return startedAtFormatter.format(new Date(startedAt));
}

// --- AgentRunRow ---

function AgentRunRow({
  run,
  agentDefinitionId,
}: {
  run: AgentRunSummary;
  agentDefinitionId: string;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const cancelRun = useCancelAgentRun();
  const { showToast } = useToast();

  const isRunning = run.status === 'running';
  const totalTokens =
    (run.totalInputTokens ?? 0) + (run.totalOutputTokens ?? 0);

  async function handleCancel(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await cancelRun.mutateAsync({ agentRunId: run.id });
      showToast({
        title: 'Session cancelled',
        message: 'The agent session has been cancelled.',
        color: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Cancel failed',
        message:
          error instanceof Error ? error.message : 'Failed to cancel session.',
        color: 'error',
      });
    }
  }

  function openDetail() {
    navigate({
      to: paths.authenticated.backgroundAgents.definitionSessionDetail,
      params: { agentDefinitionId, agentRunId: run.id },
    });
  }

  return (
    <>
      {/* Summary row — clicking it opens the full session detail; the caret
          toggles the inline trace preview without leaving the list. */}
      <tr
        onClick={openDetail}
        className={cn(
          'border-b border-border cursor-pointer transition-colors duration-75',
          isExpanded
            ? 'bg-stone-50'
            : isRunning
              ? 'bg-white hover:bg-stone-50'
              : 'bg-white hover:bg-stone-50',
        )}
      >
        {/* Expand caret — inline trace preview */}
        <td className="w-8 px-2 h-9 align-middle">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            aria-expanded={isExpanded}
            title={
              isExpanded
                ? gt('Hide inline trace preview', {
                    context: 'tooltip collapse inline agent session trace',
                  })
                : gt('Show inline trace preview', {
                    context: 'tooltip expand inline agent session trace',
                  })
            }
            className="w-4 h-4 flex items-center justify-center border-none bg-transparent text-stone-500 cursor-pointer p-0 transition-transform duration-100 hover:text-stone-800"
            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
          >
            <CaretRightIcon className="h-2.5 w-2.5" />
          </button>
        </td>

        {/* Status */}
        <td className="px-2 h-9 align-middle">
          <div className="flex items-center gap-1.5">
            <StatusDot
              status={
                run.status === 'failed'
                  ? 'failed'
                  : run.status === 'running'
                    ? 'running'
                    : 'completed'
              }
            />
            <span className="text-[11.5px] text-stone-700">{run.status}</span>
          </div>
        </td>

        {/* Model */}
        <td className="px-2 h-9 align-middle">
          <span className="font-mono text-[11.5px] text-stone-800">
            {run.model ?? '—'}
          </span>
        </td>

        {/* Steps */}
        <td className="px-2 h-9 align-middle text-right">
          <span className="font-mono text-[11.5px] tabular-nums text-stone-800">
            {run.llmSteps ?? '—'}
          </span>
        </td>

        {/* Tokens in */}
        <td className="px-2 h-9 align-middle text-right">
          <span className="font-mono text-[11.5px] tabular-nums text-stone-800">
            {fmtTokens(run.totalInputTokens)}
          </span>
        </td>

        {/* Tokens out */}
        <td className="px-2 h-9 align-middle text-right">
          <span className="font-mono text-[11.5px] tabular-nums text-stone-800">
            {fmtTokens(run.totalOutputTokens)}
          </span>
        </td>

        {/* Reasoning */}
        <td className="px-2 h-9 align-middle text-right">
          <span className="font-mono text-[11.5px] tabular-nums text-stone-500">
            {run.totalReasoningTokens
              ? fmtTokens(run.totalReasoningTokens)
              : '—'}
          </span>
        </td>

        {/* Cache */}
        <td className="px-2 h-9 align-middle text-right">
          <span className="font-mono text-[11.5px] tabular-nums text-emerald-700">
            {run.totalCacheReadTokens
              ? fmtTokens(run.totalCacheReadTokens)
              : '—'}
          </span>
        </td>

        {/* Duration */}
        <td className="px-2 h-9 align-middle text-right">
          <span className="font-mono text-[11.5px] tabular-nums text-stone-800">
            {fmtMs(run.duration)}
          </span>
        </td>

        {/* Started */}
        <td className="px-2 h-9 align-middle">
          <span
            className="text-[11.5px] text-muted-foreground"
            title={formatRunStartedAt(run.startedAt)}
          >
            {formatDistanceToNow(new Date(run.startedAt), { addSuffix: true })}
          </span>
        </td>

        {/* Actions */}
        <td className="px-2 h-9 align-middle">
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {isRunning && (
              <button
                onClick={handleCancel}
                disabled={cancelRun.isPending}
                title={gt('Stop session', {
                  context: 'tooltip stop running agent session',
                })}
                className="h-6 w-6 flex items-center justify-center rounded border-none bg-transparent text-red-600 cursor-pointer hover:bg-red-50 disabled:opacity-50"
              >
                {cancelRun.isPending ? (
                  <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <StopIcon className="h-3.5 w-3.5" />
                )}
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded trace */}
      {isExpanded && (
        <tr className="border-b border-border">
          <td colSpan={11} className="p-0">
            {run.error && (
              <div className="mx-3 mt-3 rounded-md border border-destructive/50 bg-destructive/10 p-3">
                <p className="text-sm text-destructive">{run.error}</p>
              </div>
            )}
            <div className={cn('p-3', run.error && 'pt-2')}>
              <AgentTraceTable
                agentRunId={run.id}
                compact
                maxHeight={totalTokens > 5000 ? '520px' : '380px'}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// --- AgentDefinitionSessionsTab ---

export function AgentDefinitionSessionsTab() {
  const gt = useGT();
  const { definition } = useAgentDefinitionForm();
  const agentDefinitionId = definition?.id ?? '';
  const { data, isLoading, error, refetch } =
    useListAgentRuns(agentDefinitionId);

  const runs = data?.runs ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive text-sm">
          {error instanceof Error
            ? error.message
            : gt('Failed to load agent runs', {
                context: 'error loading agent session list',
              })}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="mt-3"
        >
          <ArrowsClockwiseIcon className="h-4 w-4 mr-2" />
          <T context="button retry loading agent runs">Retry</T>
        </Button>
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>
          <T context="empty state no agent sessions">
            No sessions recorded yet.
          </T>
        </p>
        <p className="text-sm mt-2">
          <T context="empty state hint for agent sessions">
            Sessions appear here after the agent has been run.
          </T>
        </p>
      </div>
    );
  }

  const tableColumns: Array<{ label: string; align: 'left' | 'right' }> = [
    {
      label: gt('Status', { context: 'agent sessions table column header' }),
      align: 'left',
    },
    {
      label: gt('Model', { context: 'agent sessions table column header' }),
      align: 'left',
    },
    {
      label: gt('Steps', {
        context: 'agent sessions table column header, number of LLM steps',
      }),
      align: 'right',
    },
    {
      label: gt('Input', {
        context: 'agent sessions table column header, input token count',
      }),
      align: 'right',
    },
    {
      label: gt('Output', {
        context: 'agent sessions table column header, output token count',
      }),
      align: 'right',
    },
    {
      label: gt('Reasoning', {
        context: 'agent sessions table column header, reasoning token count',
      }),
      align: 'right',
    },
    {
      label: gt('Cache', {
        context: 'agent sessions table column header, cache read token count',
      }),
      align: 'right',
    },
    {
      label: gt('Duration', { context: 'agent sessions table column header' }),
      align: 'right',
    },
    {
      label: gt('Started', { context: 'agent sessions table column header' }),
      align: 'left',
    },
    { label: '', align: 'left' },
  ];

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-[11.5px] text-muted-foreground font-mono tabular-nums">
          <T context="agent sessions count summary">
            <Num>{data?.total ?? runs.length}</Num>{' '}
            <Plural
              n={data?.total ?? runs.length}
              one="session"
              other="sessions"
            />
          </T>
        </p>
        <button
          onClick={() => refetch()}
          className="h-7 px-2.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-white text-[11.5px] text-stone-700 cursor-pointer hover:bg-stone-50 transition-colors"
        >
          <ArrowsClockwiseIcon className="h-3 w-3" />
          <T context="button refresh agent sessions list">Refresh</T>
        </button>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table
          className="w-full border-collapse"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            <col style={{ width: '32px' }} />
            <col style={{ width: '100px' }} />
            <col />
            <col style={{ width: '56px' }} />
            <col style={{ width: '64px' }} />
            <col style={{ width: '64px' }} />
            <col style={{ width: '72px' }} />
            <col style={{ width: '62px' }} />
            <col style={{ width: '88px' }} />
            <col style={{ width: '130px' }} />
            <col style={{ width: '40px' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="px-2 py-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500 border-b border-border bg-stone-50 h-[30px] text-left">
                <span className="sr-only">
                  <T context="agent sessions table column, expand row toggle">
                    Expand
                  </T>
                </span>
              </th>
              {tableColumns.map((col, index) => (
                <th
                  key={index}
                  className="px-2 py-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500 border-b border-border bg-stone-50 h-[30px]"
                  style={{ textAlign: col.align as 'left' | 'right' }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <AgentRunRow
                key={run.id}
                run={run}
                agentDefinitionId={agentDefinitionId}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
